/**
 * Centralized greybox balancing for PARK_V1 WU-002.
 *
 * Tune economy, pacing, unlocks and visitor density here.
 * Gameplay systems should read these values instead of embedding magic numbers.
 *
 * Units are in field names or comments: coins, milliseconds, seconds, counts.
 */

import type { ContentRole, EntityKind, UnlockSpec } from '../types';
import { isPlotId, plotCost } from '../spatial/plots';
import { PARCEL_EAST, PARCEL_NORTH, PARCEL_START } from './map';

export const SAVE_VERSION = 3 as const;
export const PREVIOUS_SAVE_VERSION = 2 as const;
export const LEGACY_SAVE_VERSIONS = [1, 2] as const;

/** Coin balance given to a new park. Unit: coins. */
export const STARTING_COINS = 100;

/** Paid floor/flower paint cost per changed cell. Erasing a currently paid cell refunds this amount. */
export const FLOOR_PAINT_COST = 50;

/** How often income-producing entities add revenue. Unit: milliseconds. */
export const INCOME_TICK_MS = 1000;

/** Show a collect cue once stored income reaches this amount. Unit: coins. */
export const COLLECT_INDICATOR_MIN_COINS = 1;

/** Auto-save while the tab stays open. Unit: milliseconds. */
export const AUTOSAVE_INTERVAL_MS = 10_000;

export interface EntityDef {
  kind: EntityKind;
  name: string;
  role: ContentRole;
  /** Unit: coins. */
  purchaseCost: number;
  maxLevel: number;
  /** Coins added each income tick, indexed by level (0 unused). */
  revenuePerTickByLevel: number[];
  /** Cost to upgrade FROM this level to the next. Index maxLevel unused. */
  upgradeCostFromLevel: number[];
  /** Max uncollected coins stored on the entity, by level. */
  storageCapByLevel: number[];
  /** Park-score contribution by level. */
  parkScoreByLevel: number[];
  unlock: UnlockSpec;
}

function attraction(
  kind: EntityKind,
  name: string,
  purchaseCost: number,
  revenue: [number, number, number],
  upgrades: [number, number],
  storage: [number, number, number],
  score: [number, number, number],
  unlock: UnlockSpec,
): EntityDef {
  return {
    kind,
    name,
    role: 'attraction',
    purchaseCost,
    maxLevel: 3,
    revenuePerTickByLevel: [0, revenue[0], revenue[1], revenue[2]],
    upgradeCostFromLevel: [0, upgrades[0], upgrades[1], 0],
    storageCapByLevel: [0, storage[0], storage[1], storage[2]],
    parkScoreByLevel: [0, score[0], score[1], score[2]],
    unlock,
  };
}

function kiosk(
  kind: EntityKind,
  name: string,
  purchaseCost: number,
  revenue: number,
  storage: number,
  score: number,
  unlock: UnlockSpec,
): EntityDef {
  return {
    kind,
    name,
    role: 'kiosk',
    purchaseCost,
    maxLevel: 1,
    revenuePerTickByLevel: [0, revenue],
    upgradeCostFromLevel: [0, 0],
    storageCapByLevel: [0, storage],
    parkScoreByLevel: [0, score],
    unlock,
  };
}

function decoration(
  kind: EntityKind,
  name: string,
  purchaseCost: number,
  score: number,
  unlock: UnlockSpec,
): EntityDef {
  return {
    kind,
    name,
    role: 'decoration',
    purchaseCost,
    maxLevel: 1,
    revenuePerTickByLevel: [0, 0],
    upgradeCostFromLevel: [0, 0],
    storageCapByLevel: [0, 0],
    parkScoreByLevel: [0, score],
    unlock,
  };
}

/**
 * All buildable content.
 * ENG-V2-04 writes the audited ENG-V2-02R early-chain candidate.
 * Those integers stay unfrozen until ENG-V2-04H. Unlisted fields stay as they were.
 */
export const ENTITIES: Record<EntityKind, EntityDef> = {
  starter_ride: attraction(
    'starter_ride',
    'Starter Ride',
    40,
    [1, 2, 4],
    [210, 20000],
    [180, 180, 160],
    [10, 16, 24],
    {},
  ),
  spinning_ride: attraction(
    'spinning_ride',
    'Spinning Ride',
    1070,
    [2, 4, 7],
    [5000, 20000],
    [360, 160, 280],
    [12, 18, 26],
    { requiresKind: 'starter_ride' },
  ),
  pirate_ship: attraction(
    'pirate_ship',
    'Pirate Ship',
    2400,
    [3, 6, 11],
    [10000, 40000],
    [120, 240, 440],
    [18, 28, 40],
    { minParkScore: 30, requiresKind: 'spinning_ride' },
  ),
  ferris_wheel: attraction(
    'ferris_wheel',
    'Ferris Wheel',
    14000,
    [5, 9, 15],
    [10000, 40000],
    [200, 360, 600],
    [26, 40, 56],
    { minParkScore: 55, requiresParcel: PARCEL_EAST },
  ),
  roller_coaster: attraction(
    'roller_coaster',
    'Roller Coaster',
    42000,
    [9, 16, 26],
    [50000, 100000],
    [360, 640, 1040],
    [50, 78, 110],
    { minParkScore: 95, requiresKind: 'ferris_wheel', requiresParcel: PARCEL_NORTH },
  ),
  snack_kiosk: kiosk('snack_kiosk', 'Snack Kiosk', 60, 2, 360, 6, {}),
  drink_kiosk: kiosk('drink_kiosk', 'Drink Kiosk', 90, 2, 360, 7, { requiresKind: 'starter_ride' }),
  bench: decoration('bench', 'Bench', 20, 4, {}),
  tree: decoration('tree', 'Tree', 30, 5, { minParkScore: 10 }),
  lamp: decoration('lamp', 'Lamp', 45, 6, { minParkScore: 16 }),
};

/** Named aliases so existing WU-001 tests keep reading the same objects. */
export const STARTER_RIDE = ENTITIES.starter_ride;
export const SNACK_KIOSK = ENTITIES.snack_kiosk;
export const BENCH = ENTITIES.bench;

export interface ParcelBalance {
  /** Unit: coins. 0 for the starting parcel. */
  cost: number;
  /** Score granted while this parcel stays unlocked. */
  parkScore: number;
  requiresParcel?: string;
}

export const PARCEL_BALANCE: Record<string, ParcelBalance> = {
  [PARCEL_START]: { cost: 0, parkScore: 0 },
  [PARCEL_EAST]: { cost: 1800, parkScore: 8, requiresParcel: PARCEL_START },
  [PARCEL_NORTH]: { cost: 9000, parkScore: 18, requiresParcel: PARCEL_EAST },
};

export function entityDef(kind: EntityKind): EntityDef {
  return ENTITIES[kind];
}

export function parcelBalance(parcelId: string): ParcelBalance | undefined {
  return PARCEL_BALANCE[parcelId];
}

export function expansionCost(parcelId: string): number {
  if (isPlotId(parcelId)) {
    return plotCost(parcelId);
  }
  return parcelBalance(parcelId)?.cost ?? 0;
}

export const VISITORS = {
  /** Visitors present even in an empty park. Unit: count. */
  baseCount: 2,
  /** Extra visitor per this many park-score points. Unit: score points. */
  scorePerVisitor: 10,
  /** Hard cap on visible visitors. Unit: count. */
  maxCount: 20,
  /** Patrol speed. Unit: pixels per second. */
  moveSpeedPxPerSec: 52,
  /** Chance to walk toward an occupied slot when reaching a waypoint. */
  visitChance: 0.22,
};

export const OFFLINE = {
  /** Maximum coins granted from one offline period. Unit: coins. */
  incomeCapCoins: 500,
  /** Elapsed time considered for offline income. Unit: seconds. */
  maxElapsedSeconds: 8 * 60 * 60,
};

/**
 * ENG-V2-04 Round-3 reward constants.
 * This packet stores them only. It does not grant coins, offer tasks, or open a day card.
 * They stay unfrozen until ENG-V2-04H.
 */
export const FIRST_SESSION_REWARD_COINS = 25;
export const TASK_REWARD_BENCH = 30;
export const TASK_REWARD_TREE = 45;
export const TASK_REWARD_LAMP = 60;
/** Day-card offline grant fraction. Must stay strictly below 1. */
export const DAY_CARD_GOAL_FRACTION = 0.25;
export const DAILY_SET_FRACTION = 0.15;
export const DAILY_SET_CAP = 250;
/** Remaining main-goal coins required before a daily place task is included. Unit: coins. */
export const DAILY_PLACE_TASK_GAP_GATE = 80;

/** Cheap greybox motion timings. Unit: milliseconds. */
export const PACING = {
  highlightPulseMs: 900,
  toastMs: 3200,
  coinFlyMs: 420,
};

/** Playtest session milestones from runtime start. Unit: milliseconds. */
export const SESSION_MILESTONES = [
  { event: 'session_1m' as const, atMs: 60_000 },
  { event: 'session_3m' as const, atMs: 180_000 },
  { event: 'session_5m' as const, atMs: 5 * 60 * 1000 },
  { event: 'session_10m' as const, atMs: 10 * 60 * 1000 },
  { event: 'session_15m' as const, atMs: 15 * 60 * 1000 },
];
